import React, { useState, useCallback, useEffect, useMemo } from 'react';
import MessageThread from './MessageThread';
import Composer from './Composer';
import type { ConversationData } from '../context/chatTypes';
import { shortenAddress, isValidEthAddress } from '../utils/chatHelpers';
import { useChatData, useChatClient } from '../context/ChatContext';
import { useHnsNames } from '../hooks/useHnsNames';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faUsers,
  faPlus,
  faTimes,
  faSpinner,
  faUserMinus,
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

interface ChatWindowProps {
  conversation: ConversationData;
  onBack?: () => void; // Mobile back button
}

const ChatWindow: React.FC<ChatWindowProps> = ({ conversation, onBack }) => {
  const { t } = useTranslation();
  const { chatAddress } = useChatClient();
  const { addGroupMembers, removeGroupMember, loadGroupMembers, isGroupAdmin } = useChatData();
  const [showMembers, setShowMembers] = useState(false);
  const [addInput, setAddInput] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [removingAddr, setRemovingAddr] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Resolve HNS names for group members
  const memberAddresses = useMemo(
    () => (conversation.isGroup && conversation.members) ? conversation.members : [],
    [conversation.isGroup, conversation.members]
  );
  const { getDisplayName } = useHnsNames(memberAddresses);

  // Load group members on demand when the member panel is opened
  useEffect(() => {
    if (showMembers && conversation.isGroup && (!conversation.members || conversation.members.length === 0)) {
      loadGroupMembers(conversation.id);
    }
  }, [showMembers, conversation.id, conversation.isGroup, conversation.members, loadGroupMembers]);

  // Check admin status when members panel opens
  useEffect(() => {
    if (showMembers && conversation.isGroup) {
      isGroupAdmin(conversation.id).then(setIsAdmin);
    }
  }, [showMembers, conversation.id, conversation.isGroup, isGroupAdmin]);

  const displayName = conversation.isGroup
    ? conversation.groupName || t('chat.groupChat', 'Group Chat')
    : conversation.peerDisplayName || shortenAddress(conversation.peerAddress, 6);

  const memberCount = conversation.members?.length;
  const subtitle = conversation.isGroup
    ? `${memberCount ?? '...'} ${t('chat.members', 'members')}`
    : conversation.peerAddress;

  const handleAddMember = useCallback(async () => {
    const addr = addInput.trim();
    if (!addr) return;
    if (!isValidEthAddress(addr)) {
      toast.error(t('chat.invalidAddress', 'Invalid Ethereum address'));
      return;
    }
    if (conversation.members?.some(m => m.toLowerCase() === addr.toLowerCase())) {
      toast.error(t('chat.alreadyMember', 'Already a member'));
      return;
    }
    setIsAdding(true);
    await addGroupMembers(conversation.id, [addr]);
    setAddInput('');
    setIsAdding(false);
  }, [addInput, conversation.id, conversation.members, addGroupMembers, t]);

  // Load group members to populate count in header (avoid showing 0 before load)
  useEffect(() => {
    if (conversation.isGroup && !conversation.members) {
      loadGroupMembers(conversation.id);
    }
  }, [conversation.id, conversation.isGroup, conversation.members, loadGroupMembers]);

  const handleRemoveMember = useCallback(async (addr: string) => {
    setRemovingAddr(addr);
    await removeGroupMember(conversation.id, addr);
    setRemovingAddr(null);
  }, [conversation.id, removeGroupMember]);

  return (
    <div className="chat-window">
      {/* Chat header */}
      <div className="chat-window__header">
        {onBack && (
          <button className="chat-btn chat-btn--icon chat-window__back-btn" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        <div className="chat-window__header-avatar">
          <img
            className="chat-avatar"
            src={`https://api.dicebear.com/9.x/avataaars/svg?seed=${conversation.isGroup ? (conversation.groupName || conversation.id) : conversation.peerAddress}`}
            alt=""
          />
        </div>
        <div className="chat-window__header-info">
          <div className="chat-window__header-name" title={conversation.isGroup ? conversation.groupName : (conversation.peerDisplayName ? `${conversation.peerDisplayName} (${conversation.peerAddress})` : conversation.peerAddress)}>
            {displayName}
          </div>
          <div className="chat-window__header-subtitle" title={subtitle}>
            {subtitle}
          </div>
        </div>
        {conversation.isGroup && !conversation.isRemovedFromGroup && (
          <button
            className={`chat-btn chat-btn--icon chat-window__members-btn ${showMembers ? 'chat-window__members-btn--active' : ''}`}
            onClick={() => setShowMembers(!showMembers)}
            title={t('chat.manageMembers', 'Manage Members')}
          >
            <FontAwesomeIcon icon={faUsers} />
          </button>
        )}
      </div>

      {/* Group members panel */}
      {showMembers && conversation.isGroup && (
        <div className="chat-members-panel">
          <div className="chat-members-panel__header  d-flex justify-content-between align-items-center">
            <span>{t('chat.members', 'Members')} ({memberCount ?? '...'})</span>
            <button
              className="chat-btn chat-btn--icon chat-btn--close"
              onClick={() => setShowMembers(false)}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Add member input — admin only */}
          {isAdmin && (
            <div className="chat-members-panel__add">
              <input
                type="text"
                className="chat-search-input"
                placeholder="0x... address"
                value={addInput}
                onChange={(e) => setAddInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddMember()}
              />
              <button
                className="chat-btn chat-btn--primary chat-btn--sm"
                onClick={handleAddMember}
                disabled={isAdding || !addInput.trim()}
              >
                {isAdding ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
              </button>
            </div>
          )}

          {/* Member list */}
          <div className="chat-members-panel__list">
            {(conversation.members || []).map((addr) => {
              const isMe = chatAddress && addr.toLowerCase() === chatAddress.toLowerCase();
              const memberName = getDisplayName(addr);
              return (
                <div key={addr} className="chat-members-panel__item">
                  <img
                    className="chat-avatar chat-avatar--sm"
                    src={`https://api.dicebear.com/9.x/avataaars/svg?seed=${addr}`}
                    alt=""
                  />
                  <span className="chat-members-panel__addr" title={addr}>
                    {memberName}{isMe ? ` (${t('chat.you', 'You')})` : ''}
                  </span>
                  {!isMe && isAdmin && (
                    <button
                      className="chat-btn chat-btn--icon chat-btn--danger-icon"
                      onClick={() => handleRemoveMember(addr)}
                      disabled={removingAddr === addr}
                      title={t('chat.removeMember', 'Remove')}
                    >
                      {removingAddr === addr
                        ? <FontAwesomeIcon icon={faSpinner} spin />
                        : <FontAwesomeIcon icon={faUserMinus} />
                      }
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Messages */}
      <MessageThread conversation={conversation} />

      {/* Composer or removed label */}
      {conversation.isRemovedFromGroup ? (
        <div className="chat-removed-banner">
          {t('chat.removedFromGroup', 'You have been removed from this group')}
        </div>
      ) : (
        <Composer conversationId={conversation.id} />
      )}
    </div>
  );
};

export default ChatWindow;
